// =============================================================================
// VandieZ admin-web -- Ops dashboard (/ops)
// =============================================================================
// KPI overview computed at request time by the backend
// (GET /admin/ops/overview). Renders:
//   * Money cards: GMV + commission + completed trips per 24h / 7d / 30d.
//   * Activity cards: signups + active users per 24h / 7d / 30d.
//   * Completion-rate card.
//   * Queue depths (KYC + vehicles).
//   * Top 5 pickup districts (30d) as a simple bar list.
//
// No third-party chart library — minimal SVG bars are enough for the
// MVP dashboard. Adding `recharts` is a follow-up when this surface
// grows.
// =============================================================================

import {
  AlertCircle,
  ClipboardCheck,
  Loader2,
  RefreshCw,
  TrendingUp,
  Truck,
  Users,
  Wallet,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { opsAdminApi } from "@/api/client";
import { OpsTrendChart } from "@/components/OpsTrendChart";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { ApiError } from "@/types/auth";
import type {
  DailyKpiRow,
  DistrictRow,
  OpsOverviewResponse,
  TripMoneyBucket,
} from "@/types/ops";

function formatINR(decimalStr: string): string {
  const n = Number.parseFloat(decimalStr);
  if (Number.isNaN(n)) {
    return decimalStr;
  }
  return n.toLocaleString("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  });
}

function formatPct(decimalStr: string): string {
  const n = Number.parseFloat(decimalStr);
  if (Number.isNaN(n)) {
    return decimalStr;
  }
  return `${(n * 100).toFixed(2)}%`;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

interface MoneyCardProps {
  title: string;
  bucket: TripMoneyBucket;
}

function MoneyCard({ title, bucket }: MoneyCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardDescription>{title}</CardDescription>
        <CardTitle className="text-2xl">{formatINR(bucket.gmv)}</CardTitle>
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {bucket.trip_count.toLocaleString("en-IN")} trips · commission{" "}
        {formatINR(bucket.commission)}
      </CardContent>
    </Card>
  );
}

interface KpiCardProps {
  icon: React.JSX.Element;
  title: string;
  value: string | number;
  subtitle?: string;
}

function KpiCard({ icon, title, value, subtitle }: KpiCardProps): React.JSX.Element {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardDescription>{title}</CardDescription>
          {icon}
        </div>
        <CardTitle className="text-2xl">{value}</CardTitle>
      </CardHeader>
      {subtitle !== undefined && (
        <CardContent className="text-xs text-muted-foreground">
          {subtitle}
        </CardContent>
      )}
    </Card>
  );
}

interface DistrictBarsProps {
  rows: DistrictRow[];
}

function DistrictBars({ rows }: DistrictBarsProps): React.JSX.Element {
  if (rows.length === 0) {
    return (
      <div className="py-6 text-sm text-muted-foreground">
        No completed trips in the last 30 days.
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.trip_count), 1);
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = Math.round((r.trip_count / max) * 100);
        return (
          <div key={r.district} className="text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium">{r.district}</span>
              <span className="text-muted-foreground">
                {r.trip_count.toLocaleString("en-IN")} trips ·{" "}
                {formatINR(r.gmv)}
              </span>
            </div>
            <div className="mt-1 h-2 w-full overflow-hidden rounded bg-muted">
              <div
                className="h-full bg-primary"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function OpsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [data, setData] = useState<OpsOverviewResponse | null>(null);
  const [daily, setDaily] = useState<DailyKpiRow[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        // Fire both in parallel — daily chart is independent of overview.
        const [res, dailyRes] = await Promise.all([
          opsAdminApi.overview(accessToken, signal),
          opsAdminApi.daily(accessToken, 30, signal).catch(() => null),
        ]);
        setData(res);
        if (dailyRes !== null) {setDaily(dailyRes.rows);}
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => {
      controller.abort();
    };
  }, [load]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ops overview"
        description="Money, people, queues, and pickup-district KPIs computed at request time."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => void load()}
            disabled={loading}
          >
            <RefreshCw className="mr-1 h-4 w-4" /> Refresh
          </Button>
        }
      />
        <div className="text-xs text-muted-foreground">
          {data !== null
            ? `As of ${formatDateTime(data.as_of)}`
            : "Loading…"}
        </div>

        {error !== null && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && data === null ? (
          <div className="flex items-center gap-2 py-12 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : data !== null ? (
          <>
            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Money
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <MoneyCard title="GMV last 24h" bucket={data.money_24h} />
                <MoneyCard title="GMV last 7d" bucket={data.money_7d} />
                <MoneyCard title="GMV last 30d" bucket={data.money_30d} />
              </div>
              {daily !== null && daily.length > 0 && (
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <Card>
                    <CardContent className="pt-6">
                      <OpsTrendChart rows={daily} metric="gmv" />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <OpsTrendChart rows={daily} metric="commission" />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <OpsTrendChart rows={daily} metric="completed_trips" />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <OpsTrendChart rows={daily} metric="new_signups" />
                    </CardContent>
                  </Card>
                </div>
              )}
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                People
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <KpiCard
                  icon={<Users className="h-4 w-4 text-muted-foreground" />}
                  title="New signups"
                  value={data.signups.last_30d.toLocaleString("en-IN")}
                  subtitle={`${data.signups.last_7d} this week · ${data.signups.last_24h} today`}
                />
                <KpiCard
                  icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
                  title="Active users 7d"
                  value={data.active_users_7d.toLocaleString("en-IN")}
                  subtitle={`${data.active_users_24h} today · ${data.active_users_30d} in 30d`}
                />
                <KpiCard
                  icon={<Wallet className="h-4 w-4 text-muted-foreground" />}
                  title="Completion rate (30d)"
                  value={formatPct(data.completion_rate_30d.rate)}
                  subtitle={`${data.completion_rate_30d.completed} completed · ${data.completion_rate_30d.cancelled} cancelled`}
                />
              </div>
            </section>

            <section>
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Queues
              </h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <Link to="/kyc/queue" className="group">
                  <Card className="transition-colors group-hover:border-primary">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardDescription>KYC pending</CardDescription>
                        <ClipboardCheck className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <CardTitle className="text-2xl">
                        {data.queues.kyc_pending.toLocaleString("en-IN")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-primary group-hover:underline">
                      Open queue →
                    </CardContent>
                  </Card>
                </Link>
                <Link to="/vehicles/queue" className="group">
                  <Card className="transition-colors group-hover:border-primary">
                    <CardHeader className="pb-2">
                      <div className="flex items-center justify-between">
                        <CardDescription>Vehicles pending</CardDescription>
                        <Truck className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <CardTitle className="text-2xl">
                        {data.queues.vehicles_pending.toLocaleString("en-IN")}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-xs text-primary group-hover:underline">
                      Open queue →
                    </CardContent>
                  </Card>
                </Link>
              </div>
            </section>

            <section>
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    Top pickup districts (30d)
                  </CardTitle>
                  <CardDescription>
                    By completed-trip count. Bar lengths normalised to the
                    busiest district.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <DistrictBars rows={data.top_pickup_districts_30d} />
                </CardContent>
              </Card>
            </section>
          </>
        ) : null}
    </div>
  );
}
